import React, { useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { auth } from '../../../lib/firebase/config';
import ChangePasswordModal from '../components/ChangePasswordModal';
import AuthMethodDisplay from '../components/AuthMethodDisplay';
import { usePasswordChange } from '../hooks/usePasswordChange';

const SecuritySettingsPage: React.FC = () => {
  const { user } = useAuth();
  const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] = useState(false);
  const isGuestUser = user?.email === null || user?.email === '';
  const passwordChange = usePasswordChange();

  // Determine authentication method
  const getAuthMethod = () => {
    if (isGuestUser) return 'anonymous';
    
    // Use Firebase's auth.currentUser to access providerData
    const currentUser = auth.currentUser;
    if (currentUser && currentUser.providerData && currentUser.providerData.length > 0) {
      const provider = currentUser.providerData[0].providerId;
      if (provider.includes('google')) return 'google';
    }
    return 'email';
  };

  const authMethod = getAuthMethod();
  const canChangePassword = authMethod === 'email';

  const handlePasswordChangeClick = () => {
    passwordChange.resetState();
    setIsChangePasswordModalOpen(true);
  };

  return (
    <div>
      <div className="mb-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Security Settings</h3>
        <p className="text-gray-500">Manage your password and account security</p>
      </div>

      <div className="space-y-6">
        {/* Authentication Method */}
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:px-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Authentication Method
            </h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500">
              How you sign in to your account
            </p>
          </div>
          <div className="border-t border-gray-200 px-4 py-5 sm:p-6">
            <AuthMethodDisplay 
              method={authMethod as any}
              isVerified={!isGuestUser}
              email={user?.email || undefined}
              showVerifiedBadge={true}
            />
          </div>
        </div>

        {/* Password Change Section */}
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:px-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Change Password
            </h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500">
              Update your account password
            </p>
          </div>
          <div className="border-t border-gray-200 px-4 py-5 sm:p-6">
            {canChangePassword ? (
              <>
                <button
                  onClick={handlePasswordChangeClick}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Change Password
                </button>
                <p className="mt-2 text-sm text-gray-500">We recommend using a strong, unique password.</p>
              </>
            ) : (
              <div className="text-sm text-gray-500">
                Password changes are not available for {authMethod === 'anonymous' ? 'guest accounts' : `accounts using ${authMethod} authentication`}.
                {authMethod === 'anonymous' && <p className="mt-2">Please upgrade your account to set a password.</p>}
              </div>
            )}
          </div>
        </div>

        {/* Login History */}
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:px-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Login History
            </h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500">
              Recent login activity on your account
            </p>
          </div>
          <div className="border-t border-gray-200 px-4 py-5 sm:p-6">
            <div className="text-sm">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                Coming Soon
              </span>
              <p className="mt-2 text-gray-500">
                View your recent login activity, including device and location information.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Change Password Modal */}
      <ChangePasswordModal
        isOpen={isChangePasswordModalOpen}
        onClose={() => setIsChangePasswordModalOpen(false)}
      />
    </div>
  );
};

export default SecuritySettingsPage; 